import {
  API_WAGE_ADVANCEMENTS_REPORT_EXPORT,
  API_WAGE_CALCULATIONS_REPORT_EXPORT,
  API_WEEKLY_COLLECTIONS_REPORT_EXPORT,
} from "../../../../utils/apis";
import { formatDate, numberFormat } from "../../../../utils/helpers";
import { BooleanTags, StatusTag } from "../../../common/registerUtils";
import { reportExport, wagePeriodFilters } from "./reportUtils";

const staffColumns = [
  { id: "user_name", header: "Staff Name", filter: { type: "text" } },
  { id: "staff_type", header: "Staff Type", filter: { type: "text" } },
  { id: "district", header: "District", filter: { type: "text" } },
];

// Reviewer actions are empty until that stage has acted
const actionTag = (action: string | null) =>
  action ? <StatusTag status={action} /> : null;

export const useWageCalculationsReport = () => ({
  tableTitle: "Wage Calculations Report",
  tableColumns: () => [
    ...staffColumns,
    { id: "period_start", header: "Period Start" },
    { id: "period_end", header: "Period End" },
    { id: "total_collections", header: "Total Collections" },
    { id: "commission_percentage", header: "Commission (%)" },
    { id: "commission_amount", header: "Commission Amount" },
    { id: "fixed_wage_amount", header: "Fixed Wage" },
    { id: "bonus_amount", header: "Bonus" },
    { id: "total_wage", header: "Total Wage" },
    { id: "is_approved", header: "Approved" },
    { id: "approved_by", header: "Approved By" },
    { id: "approved_at", header: "Approved On" },
  ],
  tableData: (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      total_collections: numberFormat(item?.total_collections),
      commission_amount: numberFormat(item?.commission_amount),
      fixed_wage_amount: numberFormat(item?.fixed_wage_amount),
      bonus_amount: numberFormat(item?.bonus_amount),
      total_wage: numberFormat(item?.total_wage),
      is_approved: <BooleanTags status={item?.is_approved} />,
      approved_at: item?.approved_at ? formatDate(item?.approved_at) : null,
    })),
  exportData: reportExport(
    API_WAGE_CALCULATIONS_REPORT_EXPORT,
    "wage_calculations_report",
  ),
  externalFilters: [
    {
      id: "is_approved",
      name: "is_approved",
      header: "Approval",
      type: "select",
      selectOptions: [
        { id: "true", name: "Approved" },
        { id: "false", name: "Not Approved" },
      ],
    },
    ...wagePeriodFilters,
  ],
});

export const useWageAdvancementsReport = () => ({
  tableTitle: "Wage Advancements Report",
  tableColumns: () => [
    ...staffColumns,
    { id: "current_fixed_wage", header: "Current Fixed Wage" },
    { id: "new_wage", header: "New Wage" },
    { id: "reason", header: "Reason" },
    { id: "status", header: "Status" },
    { id: "manager_sd_action", header: "Manager SD" },
    { id: "hr_action", header: "HR" },
    { id: "ceo_action", header: "CEO" },
    { id: "approved_by", header: "Approved By" },
    { id: "created_at", header: "Requested On" },
  ],
  tableData: (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      current_fixed_wage: numberFormat(item?.current_fixed_wage),
      new_wage: numberFormat(item?.new_wage),
      status: <StatusTag status={item?.status} />,
      manager_sd_action: actionTag(item?.manager_sd_action),
      hr_action: actionTag(item?.hr_action),
      ceo_action: actionTag(item?.ceo_action),
      created_at: formatDate(item?.created_at),
    })),
  exportData: reportExport(
    API_WAGE_ADVANCEMENTS_REPORT_EXPORT,
    "wage_advancements_report",
  ),
  externalFilters: wagePeriodFilters,
});

export const useWeeklyCollectionsReport = () => ({
  tableTitle: "Weekly Collections Report",
  tableColumns: () => [
    { id: "reference", header: "Reference" },
    { id: "staff_type", header: "Staff Type", filter: { type: "text" } },
    { id: "district", header: "District", filter: { type: "text" } },
    { id: "period_start", header: "Period Start" },
    { id: "period_end", header: "Period End" },
    { id: "total_amount", header: "Total Amount" },
    { id: "date_generated", header: "Date Generated" },
  ],
  tableData: (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      total_amount: numberFormat(item?.total_amount),
      date_generated: item?.date_generated
        ? formatDate(item?.date_generated)
        : null,
    })),
  exportData: reportExport(
    API_WEEKLY_COLLECTIONS_REPORT_EXPORT,
    "weekly_collections_report",
  ),
  externalFilters: wagePeriodFilters,
});
